"use client";

import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

import { AdminOfferEmitButton } from "@/components/admin-offer-emit-button";
import { getDealOffersQueryOptions } from "@/components/marketplace/deal-offers-query";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import { OfferListHeader } from "@/components/marketplace/offer-list-header";
import { summarizeOfferList } from "@/components/marketplace/offer-list-summary";
import { OfferRow } from "@/components/marketplace/offer-row";
import { sortOffersByItemPrice } from "@/components/marketplace/sortOffersByItemPrice";
import { useCurrency } from "@/hooks/use-currency";
import { useHashModal } from "@/hooks/use-hash-modal";
import { useIsPartnerShare } from "@/hooks/use-is-partner-share";
import { useSession } from "@/hooks/use-session";
import { useViewerQueryState } from "@/hooks/use-viewer-query-state";

const TOP_OFFER_COUNT = 3;

/**
 * Affiliate offers for one edition, below the fold: the cheapest stores as offer rows, the cheapest
 * one open on its price history. "View all" opens the same offer explorer the buy panel owns (hash
 * modal `offer-explorer`, owner `release:{id}`), so there is one full list per page.
 */
export function ReleaseOfferOverview({ releaseId }: { releaseId: string }) {
  const t = useTranslations("shared.affiliates");
  const { currency } = useCurrency();
  const { viewerId } = useViewerQueryState();
  const isPartnerShare = useIsPartnerShare();
  const hashModal = useHashModal();
  const isAdmin = useSession().data?.user.role === "admin";
  const offersQuery = useQuery({
    ...getDealOffersQueryOptions({ releaseId, currency }, { viewerId }),
    enabled: !isPartnerShare,
  });
  const offers = sortOffersByItemPrice((offersQuery.data?.offers ?? []) as DealOffer[]);
  const bestOffer = offers[0];

  if (isPartnerShare || !bestOffer) return null;
  // A single store is already the offer card above; only show it again when it has a history.
  if (offers.length === 1 && !bestOffer.priceInsightsVisible) return null;

  const summary = summarizeOfferList(offers);
  const topOffers = offers.slice(0, TOP_OFFER_COUNT);
  const openExplorer = () =>
    hashModal.open(
      "offer-explorer",
      { offer: bestOffer, format: bestOffer.formatCategory ?? undefined },
      { ownerId: `release:${releaseId}` },
    );

  return (
    <section className="@container overflow-hidden rounded-2xl border border-border/70 bg-card/40">
      <h3 className="px-4 pt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground sm:px-5">
        {t("offersForRelease")}
      </h3>
      <OfferListHeader summary={summary} />
      <ul>
        {topOffers.map((offer, index) => (
          <OfferRow
            key={offer.offerId}
            offer={offer}
            currency={currency}
            cheapest={summary.count > 1 && offer.offerId === summary.cheapestOfferId}
            contextReleaseId={releaseId}
            defaultOpen={index === 0 && offer.priceInsightsVisible}
            adminAction={
              isAdmin ? (
                <AdminOfferEmitButton
                  offerId={offer.offerId}
                  offerTitle={offer.title}
                  providerName={offer.providerDisplayName}
                  className="size-9"
                />
              ) : undefined
            }
          />
        ))}
      </ul>
      {offers.length > TOP_OFFER_COUNT && (
        <button
          type="button"
          aria-haspopup="dialog"
          onClick={openExplorer}
          className="flex min-h-12 w-full items-center justify-center border-t border-border/60 text-sm font-bold text-primary transition-colors hover:bg-secondary/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          {t("viewOtherOffers", { count: offers.length - TOP_OFFER_COUNT })}
        </button>
      )}
      <p className="border-t border-border/60 px-4 py-3 text-xs leading-normal text-muted-foreground sm:px-5">
        {t("catalogOffersStoreDisclosure")}
      </p>
    </section>
  );
}
